import React, { useState } from 'react';
import { Table, Badge, Nav } from 'react-bootstrap';

const getColorForPercentage = (percentage) => {
    if (percentage > 30) return 'success';
    if (percentage > 15) return 'primary';
    if (percentage > 5) return 'info';
    if (percentage > 0) return 'secondary';
    return 'light';
};

const PctCell = ({ count, total }) => {
    if (!count) {
        return <td className="text-muted text-center">-</td>;
    }
    const pct = total > 0 ? (count / total) * 100 : 0;
    const color = getColorForPercentage(pct);
    return (
        <td className={`bg-${color} bg-opacity-25 text-center`}>
            <div>{count}명</div>
            <Badge pill bg={color} text="dark">{pct.toFixed(1)}%</Badge>
        </td>
    );
};

const bucketCols = ['month0_revisit', 'month1', 'month2', 'month3', 'month4', 'month5plus'];
const bucketLabels = ['당월', '+1개월', '+2개월', '+3개월', '+4개월', '5개월+'];

// ───────────────────────────────────────
// Tab 1: 재방문 주기 (A) — 첫 재방문 시점 분포 + 평균 주기 + 지속 재방문률
// ───────────────────────────────────────
const CycleTable = ({ data }) => (
    <div className="table-responsive">
        <Table bordered hover style={{ minWidth: '1000px' }}>
            <thead>
                <tr>
                    <th rowSpan={2} className="align-middle">최초 방문월</th>
                    <th rowSpan={2} className="align-middle text-center">신규</th>
                    <th colSpan={bucketCols.length} className="text-center">첫 재방문 시점</th>
                    <th rowSpan={2} className="align-middle text-center">총<br/>재방문률</th>
                    <th rowSpan={2} className="align-middle text-center">평균<br/>첫 재방문 주기</th>
                    <th rowSpan={2} className="align-middle text-center">지속<br/>재방문률 (2+)</th>
                </tr>
                <tr>
                    {bucketLabels.map(label => (
                        <th key={label} className="text-center" style={{ fontSize: '0.85rem' }}>{label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map(row => {
                    const total = row.month0;
                    if (total === 0) return null;

                    const retainedPct = total > 0 ? (row.retained / total) * 100 : 0;
                    const retainedColor = getColorForPercentage(retainedPct);
                    const loyalPct = total > 0 ? (row.loyal / total) * 100 : 0;
                    const loyalColor = getColorForPercentage(loyalPct);

                    return (
                        <tr key={row.cohortMonth}>
                            <td className="align-middle">{row.cohortMonth}</td>
                            <td className="align-middle text-center fw-bold">{total}명</td>
                            {bucketCols.map(key => (
                                <PctCell key={key} count={row.cycle?.[key] || 0} total={total} />
                            ))}
                            <td className={`align-middle text-center bg-${retainedColor} bg-opacity-25`}>
                                <div className="fw-bold">{row.retained}명</div>
                                <Badge pill bg={retainedColor} text="dark">{retainedPct.toFixed(1)}%</Badge>
                            </td>
                            <td className="align-middle text-center">
                                {row.avgCycleDays != null ? (
                                    <div className="fw-bold">{row.avgCycleDays}일</div>
                                ) : <span className="text-muted">-</span>}
                            </td>
                            <td className={`align-middle text-center bg-${loyalColor} bg-opacity-25`}>
                                <div className="fw-bold">{row.loyal}명</div>
                                <Badge pill bg={loyalColor} text="dark">{loyalPct.toFixed(1)}%</Badge>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
        <div className="text-muted small mt-2">
            ※ 각 셀은 "첫 재방문이 해당 시점에 온 차량 수"(배타적). 평균 주기는 알림톡 발송 타이밍 기준으로 활용. 지속 재방문률은 2회 이상 돌아온 단골 비율.
        </div>
    </div>
);

// ───────────────────────────────────────
// Tab 2: 월별 리텐션 (B) — 각 월 독립 체크 (중복 허용)
// ───────────────────────────────────────
const RetentionTable = ({ data }) => (
    <div className="table-responsive">
        <Table bordered hover style={{ minWidth: '900px' }}>
            <thead>
                <tr>
                    <th rowSpan={2} className="align-middle">최초 방문월</th>
                    <th rowSpan={2} className="align-middle text-center">신규</th>
                    <th colSpan={bucketCols.length} className="text-center">해당 시점에 방문한 차량</th>
                    <th rowSpan={2} className="align-middle text-center">총<br/>재방문률</th>
                </tr>
                <tr>
                    {bucketLabels.map(label => (
                        <th key={label} className="text-center" style={{ fontSize: '0.85rem' }}>{label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map(row => {
                    const total = row.month0;
                    if (total === 0) return null;

                    const retainedPct = total > 0 ? (row.retained / total) * 100 : 0;
                    const retainedColor = getColorForPercentage(retainedPct);

                    return (
                        <tr key={row.cohortMonth}>
                            <td className="align-middle">{row.cohortMonth}</td>
                            <td className="align-middle text-center fw-bold">{total}명</td>
                            {bucketCols.map(key => (
                                <PctCell key={key} count={row.retention?.[key] || 0} total={total} />
                            ))}
                            <td className={`align-middle text-center bg-${retainedColor} bg-opacity-25`}>
                                <div className="fw-bold">{row.retained}명</div>
                                <Badge pill bg={retainedColor} text="dark">{retainedPct.toFixed(1)}%</Badge>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
        <div className="text-muted small mt-2">
            ※ 각 셀은 "해당 시점에 방문한 유니크 차량"(중복 허용 — 한 차량이 여러 달 등장 가능). 생존 곡선처럼 이탈 추이를 보기 좋음.
        </div>
    </div>
);

const CohortChart = ({ data, isLoading }) => {
    const [tab, setTab] = useState('cycle');

    if (isLoading) return <div>코호트 데이터 로딩 중...</div>;
    if (!data || data.length === 0) return <div>표시할 코호트 데이터가 없습니다.</div>;

    return (
        <div>
            <Nav variant="tabs" activeKey={tab} onSelect={k => setTab(k)} className="mb-3">
                <Nav.Item>
                    <Nav.Link eventKey="cycle">재방문 주기 분포</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="retention">월별 리텐션</Nav.Link>
                </Nav.Item>
            </Nav>
            {tab === 'cycle' ? <CycleTable data={data} /> : <RetentionTable data={data} />}
        </div>
    );
};

export default CohortChart;
